import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  Shield
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../ui/accordion";

export default function PsychiatryPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Psychiatry");


  const subspecialties = [
    {
      title: "Adult Psychiatry",
      description: "Depression, anxiety, bipolar disorder & stress-related conditions.",
      image: "/AdultPsychiatry.jpg"
    },
    {
      title: "Child & Adolescent Psychiatry",
      description: "Behavioral, emotional & developmental concerns.",
      image: "/Child&AdolescentPsychiatry.jpg"
    },
    {
      title: "Geriatric Psychiatry",
      description: "Memory issues, dementia & age-related mental health care.",
      image: "/GeriatricPsychiatry.jpg"
    },
    {
      title: "Addiction Psychiatry",
      description: "Treatment for alcohol, drug & behavioral addictions.",
      image: "/AddictionPsychiatry.jpg"
    },
    {
      title: "Psychotherapy & Counseling",
      description: "CBT, family therapy, marital counseling & mindfulness.",
      image: "/Psychotherapy&Counseling.jpg"
    },
    {
      title: "Sleep Medicine",
      description: "Insomnia, sleep disorders & sleep hygiene management.",
      image: "/SleepMedicine.jpg"
    }
  ];

  const treatments = [
    "Psychiatric Evaluation & Diagnostic Assessments",
    "Depression, Anxiety & Mood Disorder Management",
    "Addiction Treatment & De-addiction Therapy",
    "Stress, Anger & Emotional Wellness Programs",
    "Family, Couples & Child Counseling",
    "Sleep Disorder Management",
    "Crisis Intervention & Suicide Risk Assessment",
    "Long-term Mental Health Follow-up Plans"
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Team",
      description: "Experienced psychiatrists & licensed psychologists"
    },
    {
      icon: Shield,
      title: "Confidential Care",
      description: "Confidential and non-judgmental care environment"
    },
    {
      icon: Heart,
      title: "Personalized Plans",
      description: "Personalized therapy & medication plans"
    },
    {
      icon: Award,
      title: "Supportive Counseling",
      description: "Supportive counseling for individuals, couples & families"
    },
    {
      icon: Clock,
      title: "Integrated Care",
      description: "Integrated care with neurology, pediatrics & general medicine"
    }
  ];

  const articles = [
    {
      title: "Understanding Depression: Early Signs & When to Seek Help",
      excerpt:
        "Learn to recognize early warning signs of depression and understand when professional help is necessary for recovery."
    },
    {
      title: "How Counseling Improves Emotional Well-Being",
      excerpt: "Discover the benefits of professional counseling and how therapy can transform your emotional health."
    },
    {
      title: "Effective Ways to Overcome Addiction Safely",
      excerpt:
        "Explore evidence-based approaches to addiction treatment and the importance of professional support in recovery."
    },
    {
      title: "Simple Habits to Improve Sleep Quality",
      excerpt:
        "Learn practical tips and healthy habits to improve your sleep quality and overcome common sleep disorders."
    }
  ];

  const faqs = [
    {
      question: "Are psychiatric consultations confidential?",
      answer:
        "Yes, all consultations, records & therapy sessions are strictly confidential. We follow professional ethics and legal guidelines to protect patient privacy at all times."
    },
    {
      question: "Do you offer counseling as well as medication?",
      answer:
        "Yes, treatment may include psychotherapy, behavioral therapy, medication, or a combination based on each patient's needs."
    },
    {
      question: "Do you treat children and teenagers?",
      answer:
        "Yes, we offer complete child & adolescent psychiatric care including behavioral and emotional support."
    }
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Psychiatry Department",
    "url": "https://mamshospitals.com/psychiatry",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Psychiatry.jpg",
    "description": "MAMS Hospitals provides advanced psychiatry and mental health services in Hyderabad including depression treatment, anxiety counseling, addiction therapy, child psychiatry, sleep disorder treatment, and psychotherapy.",
    "medicalSpecialty": "Psychiatry",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalTherapy",
        "name": "Depression & Anxiety Treatment"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Psychotherapy & Counseling"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Addiction & De-addiction Therapy"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Child & Adolescent Psychiatry"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Sleep Disorder Management"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Are psychiatric consultations confidential?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all psychiatric consultations and therapy sessions at MAMS Hospitals are completely confidential and privacy protected."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide counseling and therapy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides psychotherapy, behavioral therapy, stress management counseling, family counseling, and addiction therapy."
        }
      },
      {
        "@type": "Question",
        "name": "Do you treat children and teenagers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our psychiatry department offers child and adolescent mental health care including behavioral and emotional support."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Psychiatry Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/psychiatry",
    "description": "Best psychiatry hospital in Hyderabad offering depression treatment, anxiety counseling, psychotherapy, addiction therapy, child psychiatry, and sleep disorder treatment.",
    "keywords": [
      "Best Psychiatrist Hyderabad",
      "Psychiatry Hospital Hyderabad",
      "Depression Treatment Hyderabad",
      "Anxiety Counseling Hyderabad",
      "Addiction Therapy Hyderabad",
      "Child Psychiatrist Hyderabad",
      "Psychiatry Hospital Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
      title="Best Psychiatry Hospital in Hyderabad | Mental Health Specialists | MAMS Hospitals"
      description="MAMS Hospitals offers advanced psychiatry and mental health services in Hyderabad including depression treatment, anxiety counseling, psychotherapy, addiction therapy, sleep disorder treatment, and child psychiatry."
      keywords="best psychiatrist Hyderabad,
    psychiatry hospital Hyderabad,
    depression treatment Hyderabad,
    anxiety counseling Hyderabad,
    mental health doctor Hyderabad,
    psychotherapy Hyderabad,
    addiction therapy Hyderabad,
    child psychiatrist Hyderabad,
    sleep disorder treatment Hyderabad,
    stress management Hyderabad,
    family counseling Hyderabad,
    psychiatry hospital Bachupally,
    MAMS Hospitals Psychiatry"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Psychiatry Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Compassionate Mental Health Care for Complete Well-Being
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                MAMS Hospitals is recognized as one of the Best Psychiatry Hospitals in Hyderabad, providing compassionate, confidential, and evidence-based mental health care.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">100%</div>
                <div className="text-sm">Confidential</div>
              </div>

              <img
                src="/Psychiatry.jpg"
                alt="Psychiatry at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/psychiatry"
      />
            </div>

          </div>
        </div>
      </section>

      {/* SUB-SPECIALITIES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 text-center">

          <h2 className="text-3xl md:text-4xl font-bold mb-4">Sub-Specialities in Psychiatry</h2>

          <p className="text-gray-600 max-w-3xl mx-auto mb-12">
            Comprehensive mental health care across all age groups and conditions
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, i) => (
              <Card key={i} className="hover:shadow-xl border-gray-200 transition">
                <CardContent className="p-0">
                  <div className="h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* TREATMENTS */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-6xl px-4 text-center">

          <h2 className="text-3xl md:text-4xl font-bold mb-4">Treatments & Procedures</h2>

          <p className="text-gray-600 max-w-3xl mx-auto mb-12">
            Evidence-based treatment approaches for mental health conditions
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((item, i) => (
              <div key={i} className="flex bg-white p-4 rounded-lg shadow gap-3">
                <CheckCircle2 className="h-6 w-6 text-teal-600 mt-1" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose MAMS Hospitals for Psychiatry
          </h2>

          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Trusted mental health care with compassionate guidance
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, i) => (
              <Card key={i} className="hover:shadow-lg border-gray-200 transition">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mb-4">
                    <item.icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* SPECIALIST */}
                      {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* ARTICLES */}
      {/* <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 text-center">

          <h2 className="text-3xl md:text-4xl font-bold mb-4">Recent Articles</h2>

          <p className="text-gray-600 mb-12">Stay informed about mental health and well-being</p>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((a, i) => (
              <Card key={i} className="hover:shadow-xl border-gray-200 transition cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold group-hover:text-teal-600">{a.title}</h3>
                  <p className="text-gray-600 mt-2">{a.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section> */}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto max-w-4xl px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Frequently Asked Questions</h2>

          <p className="text-gray-600 text-center mb-12">
            Get answers to common questions about psychiatric care
          </p>

          <Accordion type="single" collapsible>
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CRISIS SUPPORT */}
      {/* <section className="bg-teal-700 text-white py-6">
        <div className="container mx-auto max-w-6xl px-4">

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">

            <div>
              <h3 className="text-xl font-bold">Need Immediate Mental Health Support?</h3>
              <p className="text-teal-100">Crisis intervention & counseling available</p>
            </div>

            <Link to="tel:+917399999981">
              <Button className="bg-white text-teal-700 hover:bg-gray-100 rounded-full" size="lg">
                <Phone className="mr-2 h-5 w-5" />
                Call Now for Support
              </Button>
            </Link>

          </div>

        </div>
      </section> */}

    </div>
    </>
  );
}
